import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert
} from 'reactstrap';
import { removeDoctorRequest } from '../../services/api/offline-consult'; // ✅ your service import
import { toast } from 'react-toastify';

const reasons = [
  'Left Organization',
  'Deceased',
  'Moved Unit',
  'Not Accepting Credit',
  'OPD/IPD Only',
  'Others'
];

const RemoveDoctorModal = ({ visible, onClose }) => {
  const [form, setForm] = useState({
    doctorId: '',
    doctorName: '',
    centerId: '',
    centerName: '',
    reason: '',
    customReason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // ✅ Validation
    if (
      !form.doctorId ||
      !form.doctorName ||
      !form.centerId ||
      !form.centerName ||
      !form.reason ||
      (form.reason === 'Others' && !form.customReason)
    ) {
      setError('Please fill all required fields');
      return;
    }

    setLoading(true);

    // ✅ API payload
    const payload = {
      doctorId: form.doctorId,
      doctorName: form.doctorName,
      centerId: form.centerId,
      centerName: form.centerName,
      reason: form.reason === 'Others' ? form.customReason : form.reason
    };

    try {
      const response = await removeDoctorRequest(payload); // ✅ Using your service
      if (response?.message === 'success') {
        toast.success('Doctor removed successfully');
        onClose && onClose();
      } else {
        setError(
          response?.errorMessage || 'Something went wrong while removing doctor.'
        );
        toast.error('Failed to remove doctor');
      }
    } catch (err) {
      setError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Internal server error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Inline Styles
  const styles = {
    modalContent: {
      borderRadius: '12px',
      boxShadow: '0 4px 18px rgba(0,0,0,0.1)',
      border: 'none'
    },
    modalBody: {
      padding: '24px'
    },
    input: {
      borderRadius: '8px',
      border: '1px solid #dcdcdc',
      padding: '8px 12px',
      fontSize: '0.95rem',
      color: '#333',
      boxShadow: 'none',
      width: '70%'
    },
    footer: {
      borderTop: '1px solid #f0f0f0',
      padding: '16px 24px',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '10px'
    },
    submitBtn: {
      backgroundColor: '#5a32ea',
      borderColor: '#5a32ea',
      fontWeight: 500,
      borderRadius: '8px',
      padding: '6px 18px',
      width: '40%'
    },
    cancelBtn: {
      border: '1px solid #5a32ea',
      color: '#5a32ea',
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '6px 18px',
      fontWeight: 500,
      width: '40%'
    }
  };

  return (
    <Modal
      isOpen={visible}
      toggle={onClose}
      centered
      contentClassName="border-0"
      style={styles.modalContent}
    >
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={onClose} className="revamp-modal-header">
          Remove Doctor
        </ModalHeader>

        <ModalBody style={styles.modalBody}>
          {/* Doctor ID */}
          <FormGroup style={{ display: 'flex', gap: '10px' }}>
            <Label for="doctorId" style={styles.label}>
              Doctor ID :
            </Label>
            <Input
              type="text"
              name="doctorId"
              id="doctorId"
              value={form.doctorId}
              onChange={handleChange}
              placeholder="Enter Doctor ID"
              style={styles.input}
              required
            />
          </FormGroup>

          {/* Doctor Name */}
          <FormGroup style={{ display: 'flex', gap: '10px' }}>
            <Label for="doctorName" style={styles.label}>
              Doctor Name :
            </Label>
            <Input
              type="text"
              name="doctorName"
              id="doctorName"
              value={form.doctorName}
              onChange={handleChange}
              placeholder="Enter Doctor Name"
              style={styles.input}
              required
            />
          </FormGroup>

          {/* Center ID */}
          <FormGroup style={{ display: 'flex', gap: '10px' }}>
            <Label for="centerId" style={styles.label}>
              Center ID :
            </Label>
            <Input
              type="text"
              name="centerId"
              id="centerId"
              value={form.centerId}
              onChange={handleChange}
              placeholder="Enter Center ID"
              style={styles.input}
              required
            />
          </FormGroup>

          {/* Center Name */}
          <FormGroup style={{ display: 'flex', gap: '10px' }}>
            <Label for="centerName" style={styles.label}>
              Centre Name :
            </Label>
            <Input
              type="text"
              name="centerName"
              id="centerName"
              value={form.centerName}
              onChange={handleChange}
              placeholder="Enter Centre Name"
              style={styles.input}
              required
            />
          </FormGroup>

          {/* Reason for Removal */}
          <FormGroup style={{ display: 'flex', gap: '10px' }}>
            <Label for="reason" style={styles.label}>
              Reason for Removal :
            </Label>
            <Input
              type="select"
              name="reason"
              id="reason"
              value={form.reason}
              onChange={handleChange}
              style={styles.input}
              required
            >
              <option value="">Select Reason</option>
              {reasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Input>
          </FormGroup>

          {/* Custom Reason */}
          {form.reason === 'Others' && (
            <FormGroup style={{ display: 'flex', gap: '10px' }}>
              <Label for="customReason" style={styles.label}>
                Custom Reason :
              </Label>
              <Input
                type="text"
                name="customReason"
                id="customReason"
                value={form.customReason}
                onChange={handleChange}
                placeholder="Enter Custom Reason"
                style={styles.input}
                required
              />
            </FormGroup>
          )}

          {error && (
            <Alert color="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter style={styles.footer}>
          <Button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Removing...' : 'Remove'}
          </Button>
          <Button onClick={onClose} style={styles.cancelBtn}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default RemoveDoctorModal;
